import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { ApiService } from "../../../api";
import CoverageDocumentService from "../../../api/services/operation/coverage-document-service";

/**
 * Hook for managing deliverables data fetching and pagination
 * @param {Object} filtersPayload - The filters payload for API requests
 * @returns {Object} Data state and handlers
 */
const useDeliverablesData = (filtersPayload) => {
	const projectId = useSelector(({ context }) => context.project?.id);

	const [rows, setRows] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [limit, setLimit] = useState(50);
	const [isLoading, setIsLoading] = useState(false);
	const [sort, setSort] = useState("format,asc");

	const cancelTokenSourceRef = useRef(null);
	const isMountedRef = useRef(true);

	useEffect(() => {
		isMountedRef.current = true;
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			isMountedRef.current = false;
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);

	const handleSort = useCallback((field) => {
		setSort((prev) => {
			const [prevField, prevDirection] = prev.split(",");
			const nextDirection = prevField === field && prevDirection === "asc" ? "desc" : "asc";
			return `${field},${nextDirection}`;
		});
		setPage(0);
	}, []);

	const fetchRows = useCallback(() => {
		if (!projectId || !isMountedRef.current) {
			return;
		}
		setIsLoading(true);

		if (cancelTokenSourceRef.current) {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		}

		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();

		const handleResult = (data) => {
			if (!isMountedRef.current) {
				return;
			}
			const items = data?.content || data?.contents || [];
			setRows(items);
			setTotal(data?.totalElements ?? 0);
		};

		CoverageDocumentService.getDeliverablesByCatFilter(
			{ projectId },
			filtersPayload,
			cancelTokenSourceRef.current.token,
			{ page, limit, sort }
		)
			.then(handleResult)
			.catch((error) => {
				if (!axios.isCancel(error)) {
					console.error(error);
				}
			})
			.finally(() => {
				if (isMountedRef.current) {
					setIsLoading(false);
				}
			});
	}, [projectId, filtersPayload, page, limit, sort]);

	useEffect(() => {
		if (isMountedRef.current) {
			fetchRows();
		}
	}, [fetchRows]);

	const getPreviewCount = useCallback(
		(tempFiltersPayload) => {
			if (!projectId) {
				return Promise.resolve(0);
			}

			return CoverageDocumentService.getDeliverablesByCatFilter(
				{ projectId },
				tempFiltersPayload,
				cancelTokenSourceRef.current?.token,
				{ page: 0, limit: 1, sort }
			)
				.then((data) => data?.totalElements ?? 0)
				.catch((error) => {
					if (!axios.isCancel(error)) {
						console.error(error);
					}
					return 0;
				});
		},
		[projectId, sort]
	);

	const handleDeleteRow = useCallback((deliverable) => {
		if (isMountedRef.current) {
			setRows((prevRows) => prevRows.filter((r) => r.id !== deliverable.id));
			setTotal((prevTotal) => prevTotal - 1);
		}
	}, []);

	const handlePageChange = useCallback((_, newPage) => {
		if (isMountedRef.current) {
			setPage(newPage);
		}
	}, []);

	const handleRowsPerPageChange = useCallback((e) => {
		if (isMountedRef.current) {
			setLimit(parseInt(e.target.value, 10));
			setPage(0);
		}
	}, []);

	const resetPage = useCallback(() => {
		setPage(0);
	}, []);

	return {
		projectId,
		rows,
		total,
		page,
		limit,
		isLoading,
		sort,
		isMountedRef,
		cancelTokenSourceRef,
		handleSort,
		fetchRows,
		getPreviewCount,
		handleDeleteRow,
		handlePageChange,
		handleRowsPerPageChange,
		resetPage,
	};
};

export default useDeliverablesData;
